/**
 * Vendas: hora a hora (3 dias comparados, escolhidos pelo usuário), últimos 30 dias e últimos 6 meses com a
 * margem, e os quadros: itens em queda (semana × semana), mais vendidos sem estoque e oportunidades de preço.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, brl, int } from '../api.ts';
import { ErrorBox, Loading } from '../components/ui.tsx';
import { downloadCsv } from '../csv.ts';
import { Download } from 'lucide-react';

const TZ = 'America/Sao_Paulo';
const ymd = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(d);
const shift = (s: string, days: number) => new Date(new Date(`${s}T12:00:00Z`).getTime() + days * 86_400_000).toISOString().slice(0, 10);
const fmtDay = (s: string) => {
  const d = new Date(`${s}T12:00:00Z`);
  return `${d.toLocaleDateString('pt-BR', { weekday: 'short', timeZone: 'UTC' }).replace('.', '')} ${s.slice(8, 10)}/${s.slice(5, 7)}`;
};
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const compact = (v: number) => (v >= 1000 ? `${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil` : v.toLocaleString('pt-BR', { maximumFractionDigits: 0 }));
const COLORS = ['#2f5bef', '#f08c00', '#0b7350'];

interface Account { id: string; channel: string; name: string | null }
interface Hourly { series: { date: string; total: number; orders: number; hours: { hour: number; amount: number; orders: number }[] }[] }
interface Series { view: 'days' | 'months'; bars: { key: string; amount: number; orders: number; margin: number | null }[] }
interface Insights {
  falling: { id: string; sku: string; title: string | null; curUnits: number; prevUnits: number; curAmount: number; prevAmount: number; deltaPct: number | null }[];
  noStock: { id: string; sku: string; title: string | null; units30: number; amount30: number; available: number }[];
  opportunities: { listingId: string; accountId: string; externalId: string; sku: string; title: string | null; price: number; buyBoxPrice: number; target: number; diffPct: number; marginNow: number | null; marginAtTarget: number | null; units30: number; amount30: number }[];
}

const CHANNEL: Record<string, string> = { mercadolivre: 'Mercado Livre', shopee: 'Shopee', ifood: 'iFood' };

/** Área de Vendas (menu): hora a hora, 30 dias, 6 meses e os quadros, por canal e por conta. */
export function SalesPage() {
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Vendas</h1>
          <p>Como as vendas andam: hora a hora contra outros dias, os últimos 30 dias e 6 meses com a margem, o que está caindo, o que vende e está sem estoque, e onde o preço está perdendo o buy box.</p>
        </div>
      </div>
      <SalesTab />
    </>
  );
}

export function SalesTab() {
  const today = ymd(new Date());
  const qc = useQueryClient();
  const [channel, setChannel] = useState('');
  const [accountId, setAccountId] = useState('');
  const [covered, setCovered] = useState<Record<string, string>>({});
  const cover = useMutation({
    mutationFn: (v: { listingId: string; price: number }) => api<{ price: number; sent: boolean; error: string | null }>('/sales/cover', { method: 'POST', json: v }),
    onSuccess: (r, v) => {
      setCovered((c) => ({ ...c, [v.listingId]: r.sent ? `Coberto: ${brl(r.price)} enviado ao ML` : `Preço ${brl(r.price)} gravado; vai na próxima rodada${r.error ? ` (${r.error})` : ''}` }));
      qc.invalidateQueries({ queryKey: ['sales-insights'] });
    },
  });
  const [view, setView] = useState<'hours' | 'days' | 'months'>('hours');
  const [dates, setDates] = useState([today, shift(today, -1), shift(today, -7)]);
  const [cumulative, setCumulative] = useState(false);
  const accounts = useQuery({ queryKey: ['finance-accounts'], queryFn: () => api<Account[]>('/finance/accounts') });
  const scope = `${channel ? `&channel=${channel}` : ''}${accountId ? `&accountId=${accountId}` : ''}`;
  const hourly = useQuery({
    queryKey: ['sales-hourly', dates.join(','), channel, accountId],
    queryFn: () => api<Hourly>(`/sales/hourly?dates=${dates.join(',')}${scope}`),
    enabled: view === 'hours', placeholderData: keepPreviousData, refetchInterval: 120_000,
  });
  const series = useQuery({
    queryKey: ['sales-series', view, channel, accountId],
    queryFn: () => api<Series>(`/sales/series?view=${view}${scope}`),
    enabled: view !== 'hours', placeholderData: keepPreviousData,
  });
  const insights = useQuery({ queryKey: ['sales-insights', channel, accountId], queryFn: () => api<Insights>(`/sales/insights?x=1${scope}`) });
  const channels = [...new Set((accounts.data ?? []).map((a) => a.channel))];
  const [exporting, setExporting] = useState('');
  // Exportar: busca a lista inteira (sem o top 20) no mesmo canal/conta.
  const exportAll = async (kind: 'falling' | 'noStock' | 'opportunities') => {
    setExporting(kind);
    try {
      const all = await api<Insights>(`/sales/insights?all=1${scope}`);
      if (kind === 'falling') downloadCsv('vendas_em_queda', ['SKU', 'Produto', 'Vendido 7 dias anteriores', 'Vendido últimos 7 dias', 'Unid. anteriores', 'Unid. últimos 7 dias', 'Variação %'],
        all.falling.map((r) => [r.sku, r.title, r.prevAmount, r.curAmount, r.prevUnits, r.curUnits, r.deltaPct]));
      else if (kind === 'noStock') downloadCsv('vendas_sem_estoque', ['SKU', 'Produto', 'Unid. em 30 dias', 'Vendido em 30 dias', 'Estoque'],
        all.noStock.map((r) => [r.sku, r.title, r.units30, r.amount30, r.available]));
      else downloadCsv('oportunidades_de_preco', ['SKU', 'Produto', 'Anúncio', 'Seu preço', 'Preço para ganhar', 'Acima %', 'Margem atual %', 'Margem no preço para ganhar %', 'Unid. em 30 dias', 'Vendido em 30 dias'],
        all.opportunities.map((r) => [r.sku, r.title, r.externalId, r.price, r.target, r.diffPct, r.marginNow, r.marginAtTarget, r.units30, r.amount30]));
    } catch (e) {
      window.alert(`Não deu para exportar: ${(e as Error).message}`);
    } finally {
      setExporting('');
    }
  };

  return (
    <div className="stack-y">
      <div className="toolbar" style={{ flexWrap: 'wrap' }}>
        <select className="select" aria-label="Canal" value={channel} onChange={(e) => { setChannel(e.target.value); setAccountId(''); }}>
          <option value="">Todos os canais</option>
          {channels.map((c) => <option key={c} value={c}>{CHANNEL[c] ?? c}</option>)}
        </select>
        {(accounts.data ?? []).filter((a) => !channel || a.channel === channel).length > 1 && (
          <select className="select" aria-label="Conta" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">Todas as contas{channel ? '' : ' / lojas'}</option>
            {(accounts.data ?? []).filter((a) => !channel || a.channel === channel).map((a) => <option key={a.id} value={a.id}>{CHANNEL[a.channel] ?? a.channel} · {a.name}</option>)}
          </select>
        )}
        <div className="seg" role="group" aria-label="Visão">
          {([['hours', 'Hora a hora'], ['days', 'Últimos 30 dias'], ['months', 'Últimos 6 meses']] as const).map(([k, l]) => (
            <button key={k} type="button" aria-pressed={view === k} onClick={() => setView(k)}>{l}</button>
          ))}
        </div>
        {view === 'hours' && (
          <>
            {dates.map((d, i) => (
              <label key={i} className="small" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: COLORS[i] }} />
                <input className="input" type="date" value={d} max={today} onChange={(e) => e.target.value && setDates(dates.map((x, j) => (j === i ? e.target.value : x)))} style={{ width: 150 }} />
              </label>
            ))}
            <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" className="check" checked={cumulative} onChange={(e) => setCumulative(e.target.checked)} /> Acumulado
            </label>
          </>
        )}
      </div>

      <section className="panel">
        {view === 'hours'
          ? (hourly.error ? <ErrorBox error={hourly.error} /> : !hourly.data ? <Loading /> : <HourlyChart data={hourly.data} cumulative={cumulative} today={today} />)
          : (series.error ? <ErrorBox error={series.error} /> : !series.data ? <Loading /> : <BarsChart data={series.data} />)}
      </section>

      {insights.error ? <ErrorBox error={insights.error} /> : !insights.data ? <Loading /> : (
        <div className="cols" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 'var(--gap)' }}>
          <Board title="Em queda" onExport={() => exportAll('falling')} exporting={exporting === 'falling'} note="vendas dos últimos 7 dias × 7 anteriores" empty="Nenhum item vendendo menos."
            rows={insights.data.falling.map((r) => ({
              key: r.id, to: `/catalogo/${r.id}`, title: r.title ?? r.sku,
              sub: `${r.sku} · ${brl(r.prevAmount)} → ${brl(r.curAmount)} · ${int(r.prevUnits)} → ${int(r.curUnits)} un.`,
              value: r.deltaPct == null ? '—' : `${r.deltaPct}%`, tone: 'var(--err)',
            }))} />
          <Board title="Vendem e estão sem estoque" onExport={() => exportAll('noStock')} exporting={exporting === 'noStock'} note="mais vendidos nos últimos 30 dias, com estoque zerado agora" empty="Nenhum item que vende está sem estoque."
            rows={insights.data.noStock.map((r) => ({
              key: r.id, to: `/catalogo/${r.id}`, title: r.title ?? r.sku,
              sub: `${r.sku} · ${int(r.units30)} un. em 30 dias · estoque ${int(r.available)}`,
              value: brl(r.amount30),
            }))} />
          <Board title="Oportunidades de preço" onExport={channel && channel !== 'mercadolivre' ? undefined : () => exportAll('opportunities')} exporting={exporting === 'opportunities'} note="anúncios do Mercado Livre perdendo o buy box, dos que mais vendem"
            empty={channel && channel !== 'mercadolivre' ? 'Disponível para o Mercado Livre (buy box).' : 'Nenhum anúncio ativo perdendo o buy box.'}
            rows={channel && channel !== 'mercadolivre' ? [] : insights.data.opportunities.map((r) => ({
              key: r.externalId, title: r.title ?? r.sku,
              action: covered[r.listingId]
                ? <span className="small" style={{ color: 'var(--ok)' }}>{covered[r.listingId]}</span>
                : <button className="btn sm" type="button" disabled={cover.isPending}
                    title="Grava o preço para ganhar como preço fixo do produto nesta conta e envia agora ao Mercado Livre"
                    onClick={() => window.confirm(`Cobrir: ${r.title ?? r.sku}\nDe ${brl(r.price)} para ${brl(r.target)}${r.marginAtTarget != null ? ` (margem ${r.marginAtTarget.toLocaleString('pt-BR')}%)` : ''}.\n\nO preço fica fixo nesta conta (vale sobre a tabela e a API) até você tirar em Preço e estoque.`) && cover.mutate({ listingId: r.listingId, price: r.target })}>
                    Cobrir {brl(r.target)}
                  </button>,
              sub: `${r.sku} · ${r.externalId} · seu preço ${brl(r.price)} (${r.diffPct}% acima) · margem ${r.marginNow == null ? '—' : `${r.marginNow.toLocaleString('pt-BR')}%`} → ${r.marginAtTarget == null ? '—' : `${r.marginAtTarget.toLocaleString('pt-BR')}%`}`,
              value: `cobrir ${brl(r.target)}`, tone: r.marginAtTarget != null && r.marginAtTarget < 0 ? 'var(--err)' : 'var(--ok)',
            }))} />
        </div>
      )}
    </div>
  );
}

function Board({ title, note, empty, rows, onExport, exporting }: {
  title: string; note: string; empty: string; onExport?: () => void; exporting?: boolean;
  rows: { key: string; to?: string; title: string; sub: string; value: string; tone?: string; action?: ReactNode }[];
}) {
  return (
    <section className="panel board">
      <div className="board-head">
        <div style={{ minWidth: 0 }}><h2 style={{ margin: 0 }}>{title}</h2><span className="small muted">{note}{rows.length ? ` · top ${rows.length}` : ''}</span></div>
        {onExport && rows.length > 0 && (
          <button className="btn sm" style={{ whiteSpace: "nowrap", flexShrink: 0 }} type="button" disabled={exporting} onClick={onExport} title="Baixa a lista inteira em planilha (CSV)">
            <Download size={14} /> {exporting ? 'Exportando…' : 'Exportar todos'}
          </button>
        )}
      </div>
      {!rows.length ? <span className="small muted">{empty}</span> : (
        <ol className="board-list">
          {rows.map((r) => (
            <li key={r.key} style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', minWidth: 0 }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }} title={r.title}>
                  {r.to ? <Link to={r.to}>{r.title}</Link> : r.title}
                </span>
                <b style={{ whiteSpace: 'nowrap', color: r.tone }}>{r.value}</b>
              </div>
              <div className="small muted">{r.sub}</div>
              {r.action && <div style={{ marginTop: 4 }}>{r.action}</div>}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// ---------- gráficos (SVG simples, sem biblioteca) ----------

const W = 900, H = 280, PL = 56, PR = 48, PT = 16, PB = 34;

function niceMax(v: number) {
  if (v <= 0) return 10;
  const p = 10 ** Math.floor(Math.log10(v));
  return Math.ceil(v / p) * p;
}

function HourlyChart({ data, cumulative, today }: { data: Hourly; cumulative: boolean; today: string }) {
  const nowHour = Number(new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', hour12: false }).format(new Date()));
  const lines = data.series.map((s) => {
    let acc = 0;
    const pts = s.hours.map((h) => { acc += h.amount; return cumulative ? acc : h.amount; });
    return { ...s, pts: s.date === today ? pts.slice(0, nowHour + 1) : pts };
  });
  const max = niceMax(Math.max(...lines.flatMap((l) => l.pts), 0));
  const x = (h: number) => PL + (h / 23) * (W - PL - PR);
  const y = (v: number) => PT + (1 - v / max) * (H - PT - PB);
  const [hover, setHover] = useState<number | null>(null);
  return (
    <div className="stack-y" style={{ gap: 8 }}>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        {lines.map((l, i) => (
          <div key={l.date} className="small" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 14, height: 3, background: COLORS[i] }} />
            <b>{fmtDay(l.date)}</b> {brl(l.total)} <span className="muted">· {int(l.orders)} pedidos</span>
          </div>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} role="img" aria-label="Vendas hora a hora" onMouseLeave={() => setHover(null)}>
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <g key={t}>
            <line x1={PL} x2={W - PR} y1={y(max * t)} y2={y(max * t)} stroke="var(--line)" />
            <text x={PL - 8} y={y(max * t) + 4} textAnchor="end" fontSize="11" fill="var(--muted)">{compact(max * t)}</text>
          </g>
        ))}
        {Array.from({ length: 24 }, (_, h) => h).filter((h) => h % 2 === 0).map((h) => (
          <text key={h} x={x(h)} y={H - 12} textAnchor="middle" fontSize="11" fill="var(--muted)">{h}h</text>
        ))}
        {lines.map((l, i) => (
          <polyline key={l.date} fill="none" stroke={COLORS[i]} strokeWidth={i === 0 ? 3 : 2} strokeDasharray={i === 2 ? '6 4' : undefined}
            points={l.pts.map((v, h) => `${x(h)},${y(v)}`).join(' ')} />
        ))}
        {Array.from({ length: 24 }, (_, h) => (
          <rect key={h} x={x(h) - (W - PL - PR) / 46} y={PT} width={(W - PL - PR) / 23} height={H - PT - PB} fill="transparent" onMouseEnter={() => setHover(h)} />
        ))}
        {hover != null && (
          <g pointerEvents="none">
            <line x1={x(hover)} x2={x(hover)} y1={PT} y2={H - PB} stroke="var(--line-strong)" />
            {lines.map((l, i) => l.pts[hover] != null && <circle key={l.date} cx={x(hover)} cy={y(l.pts[hover]!)} r={4} fill={COLORS[i]} />)}
            <g transform={`translate(${Math.min(x(hover) + 10, W - 190)}, ${PT + 4})`}>
              <rect width="180" height={22 + lines.length * 18} rx="6" fill="var(--surface)" stroke="var(--line)" />
              <text x="10" y="16" fontSize="12" fontWeight="600" fill="var(--ink)">{hover}h{cumulative ? ' (acumulado)' : ''}</text>
              {lines.map((l, i) => (
                <text key={l.date} x="10" y={34 + i * 18} fontSize="12" fill={COLORS[i]}>{fmtDay(l.date)}: {l.pts[hover] == null ? '—' : brl(l.pts[hover]!)}</text>
              ))}
            </g>
          </g>
        )}
      </svg>
    </div>
  );
}

function BarsChart({ data }: { data: Series }) {
  const bars = data.bars;
  const max = niceMax(Math.max(...bars.map((b) => b.amount), 0));
  const margins = bars.map((b) => b.margin).filter((m): m is number => m != null);
  const mMax = Math.max(10, ...margins.map((m) => Math.abs(m)));
  const step = (W - PL - PR) / bars.length;
  const x = (i: number) => PL + i * step + step / 2;
  const y = (v: number) => PT + (1 - v / max) * (H - PT - PB);
  const ym = (m: number) => PT + (1 - (m + mMax) / (2 * mMax)) * (H - PT - PB);
  const label = (k: string) => (data.view === 'days' ? `${k.slice(8, 10)}/${k.slice(5, 7)}` : `${MONTHS[Number(k.slice(5, 7)) - 1]}/${k.slice(2, 4)}`);
  const total = bars.reduce((a, b) => a + b.amount, 0);
  const last = bars.at(-1), prev = bars.at(-2);
  const g = last && prev && prev.amount > 0 ? Math.round(((last.amount - prev.amount) / prev.amount) * 100) : null;
  const marginPts = bars.map((b, i) => (b.margin == null ? null : `${x(i)},${ym(b.margin)}`)).filter(Boolean);
  return (
    <div className="stack-y" style={{ gap: 8 }}>
      <div className="small" style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
        <span><b>{brl(total)}</b> em {data.view === 'days' ? '30 dias' : '6 meses'} · {int(bars.reduce((a, b) => a + b.orders, 0))} pedidos</span>
        {data.view === 'months' && g != null && <span style={{ color: g >= 0 ? 'var(--ok)' : 'var(--err)' }}>mês atual {g >= 0 ? '+' : ''}{g}% contra o anterior (mês em andamento)</span>}
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, background: 'var(--brand)', opacity: 0.85, borderRadius: 2 }} /> vendas</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 14, height: 3, background: '#f08c00' }} /> margem (líquido − custo, sem suas despesas)</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} role="img" aria-label="Vendas e margem">
        {[0, 0.5, 1].map((t) => (
          <g key={t}>
            <line x1={PL} x2={W - PR} y1={y(max * t)} y2={y(max * t)} stroke="var(--line)" />
            <text x={PL - 8} y={y(max * t) + 4} textAnchor="end" fontSize="11" fill="var(--muted)">{compact(max * t)}</text>
          </g>
        ))}
        {[-mMax, 0, mMax].map((m) => (
          <text key={m} x={W - PR + 6} y={ym(m) + 4} fontSize="11" fill="#f08c00">{Math.round(m)}%</text>
        ))}
        {bars.map((b, i) => (
          <g key={b.key}>
            <rect x={x(i) - step * 0.35} y={y(b.amount)} width={step * 0.7} height={Math.max(0, H - PB - y(b.amount))} fill="var(--brand)" opacity={0.85} rx={2}>
              <title>{label(b.key)}: {brl(b.amount)} · {int(b.orders)} pedidos{b.margin != null ? ` · margem ${b.margin.toLocaleString('pt-BR')}%` : ''}</title>
            </rect>
            {(data.view === 'months' || i % 3 === 0 || i === bars.length - 1) && <text x={x(i)} y={H - 12} textAnchor="middle" fontSize="11" fill="var(--muted)">{label(b.key)}</text>}
            {data.view === 'months' && <text x={x(i)} y={y(b.amount) - 6} textAnchor="middle" fontSize="11" fill="var(--ink)">{compact(b.amount)}</text>}
          </g>
        ))}
        {marginPts.length > 1 && <polyline fill="none" stroke="#f08c00" strokeWidth={2} points={marginPts.join(' ')} />}
        {bars.map((b, i) => b.margin != null && (
          <circle key={b.key} cx={x(i)} cy={ym(b.margin)} r={3.5} fill="#f08c00"><title>margem {b.margin.toLocaleString('pt-BR')}%</title></circle>
        ))}
      </svg>
    </div>
  );
}
